import React from 'react';
import { CheckCircle2, Zap, Bot, Users, BarChart3, Calendar, MessageSquare, Mail, Phone, Settings } from 'lucide-react';

const CorePlatform = () => {
  const crmFeatures = [
    { icon: Users, label: 'Lead & Contact Management', desc: 'Organize all your leads in one place' },
    { icon: BarChart3, label: 'Pipeline & Deal Tracking', desc: 'Visual sales pipeline with stages' },
    { icon: Bot, label: 'AI Agents & Voice Bots', desc: 'Automated conversations 24/7' },
    { icon: Calendar, label: 'Calendar & Booking', desc: 'Scheduling & appointment management' },
    { icon: MessageSquare, label: '2-Way SMS & Chat', desc: 'Real-time customer messaging' },
    { icon: Mail, label: 'Email Campaigns', desc: 'Automated email sequences' },
    { icon: Phone, label: 'Call Tracking & Recording', desc: 'Track every conversation' },
    { icon: Settings, label: 'Workflows & Automations', desc: 'Automate repetitive tasks' },
  ];

  const podIntegrations = [
    { title: 'AI Onboarding', desc: 'Get your brand set up in minutes with our intelligent Brand Breakdown that captures everything we need.' },
    { title: 'Product Knowledge', desc: 'Access our comprehensive CRM knowledgebase and get AI-powered support for any feature.' },
    { title: 'AI Support', desc: 'Your 24/7 AI support team guides you through projects and provides strategic recommendations.' },
    { title: 'Unlimited DFY Orders', desc: 'Need anything built in your CRM? Submit an order and our 40+ departments handle it for you.' },
  ];

  return (
    <section className="py-16 md:py-24 bg-slate-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Our AI Horse-Powered{' '}
            <span className="text-emerald-400">Core Platform</span>
          </h2>
          <p className="text-xl md:text-2xl text-cyan-400 font-semibold mb-4">
            The All-In-One CRM — Built to Run Your Entire Business
          </p>
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            More than a CRM — this is your command center that <span className="text-emerald-400">integrates seamlessly with our AI Horse-Powered POD</span> for unlimited support and Done-For-You services.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start mb-16">
          {/* Left Side - CRM GIF & Trust */}
          <div>
            <div className="relative mb-6">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-2xl blur-2xl transform scale-105" />
              
              {/* GIF Container */}
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl">
                <img 
                  src="/crm_pod_integration.gif" 
                  alt="AI Horse-Powered CRM Dashboard with POD Integration" 
                  className="w-full h-auto"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>

            {/* Trust Element - 20,000+ Businesses */}
            <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 flex items-center gap-4">
              <div className="flex -space-x-2">
                <img src="https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/cplryjux_alison%20garcia.jpeg" alt="" className="w-10 h-10 rounded-full border-2 border-slate-800" />
                <img src="https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/qt0bq8i5_dennis%20hubbard.jpeg" alt="" className="w-10 h-10 rounded-full border-2 border-slate-800" />
                <img src="https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/am034sgs_jimmy%20ofarrell.png" alt="" className="w-10 h-10 rounded-full border-2 border-slate-800" />
                <img src="https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/jiyo02lp_dennis%20voice.png" alt="" className="w-10 h-10 rounded-full border-2 border-slate-800" />
                <div className="w-10 h-10 rounded-full border-2 border-slate-800 bg-emerald-500 flex items-center justify-center text-slate-950 text-xs font-bold">+</div>
              </div>
              <div>
                <p className="text-white font-bold text-lg">20,000+ Businesses</p>
                <p className="text-slate-400 text-sm">trust AI Horse-Powered</p>
              </div>
            </div>
          </div>

          {/* Right Side - Features & POD Integration */}
          <div className="space-y-8">
            {/* CRM Features Grid */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-emerald-400" />
                CRM Features Included
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {crmFeatures.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div key={idx} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 hover:border-emerald-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-cyan-400" />
                        <span className="text-white text-sm font-medium">{feature.label}</span>
                      </div>
                      <p className="text-slate-500 text-xs pl-6">{feature.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* How POD Integrates */}
            <div className="bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Bot className="w-5 h-5 text-cyan-400" />
                How the POD Powers Your CRM
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Never struggle with setup or features again. Our POD provides unlimited support to make your CRM work exactly how you need it.
              </p>
              <div className="space-y-3">
                {podIntegrations.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-white font-semibold text-sm">{item.title}:</span>
                      <span className="text-slate-400 text-sm ml-1">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Message */}
        <div className="text-center bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
          <p className="text-xl md:text-2xl text-white font-semibold mb-2">
            You Never Need to Worry — Just Name What You Need
          </p>
          <p className="text-slate-400 max-w-3xl mx-auto">
            With AI Horse-Power and our DFY support, you just tell us what you need and we get it done. 
            Or do it with you. Or you can do it yourself with access to all the tools, support, assets — <span className="text-emerald-400 font-semibold">everything</span>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CorePlatform;
